import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLenis } from "@/components/layout/SmoothScrollProvider";

const DEFAULT_SCROLL_CONTAINER_SELECTORS = ["main", "[data-scroll-container]"];

const normalizeContainerSelectors = (containerSelectors) => {
  if (Array.isArray(containerSelectors)) {
    return containerSelectors.filter(Boolean);
  }

  return containerSelectors ? [containerSelectors] : [];
};

const scrollToHashTarget = (hash, lenisRef) => {
  const targetId = decodeURIComponent(hash.replace(/^#/, ""));

  if (!targetId) {
    return false;
  }

  const targetElement = document.getElementById(targetId);
  if (!targetElement) {
    return false;
  }

  if (lenisRef && lenisRef.current) {
    lenisRef.current.scrollTo(targetElement);
  } else {
    targetElement.scrollIntoView();
  }
  
  return true;
};

const resetWindowScroll = (lenisRef) => {
  if (lenisRef && lenisRef.current) {
    lenisRef.current.scrollTo(0, { immediate: true });
    return;
  }

  if (typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(() => {
      window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    });
  } else {
    window.scrollTo(0, 0);
  }
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

const resetScrollableContainers = (selectors) => {
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((element) => {
      if (!(element instanceof HTMLElement)) {
        return;
      }

      element.scrollTop = 0;
      element.scrollLeft = 0;
    });
  });
};

const ScrollToTop = ({ containerSelectors = DEFAULT_SCROLL_CONTAINER_SELECTORS }) => {
  const { pathname, hash } = useLocation();
  const lenisRef = useLenis();

  useLayoutEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    if (hash && scrollToHashTarget(hash, lenisRef)) {
      return;
    }

    resetWindowScroll(lenisRef);
    resetScrollableContainers(normalizeContainerSelectors(containerSelectors));
  // lenisRef is a stable ref object (useRef) — excluded from deps intentionally
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, hash, containerSelectors]);

  return null;
};

export default ScrollToTop;
